import React, { useState } from "react";
import { usePOs } from "../../hooks/usePOs";
import { useLogistics } from "../../hooks/useLogistics";
import { useUsers } from "../../hooks/useUsers";
import { Calendar, DollarSign, TrendingUp, ShoppingBag, Loader2 } from "lucide-react";

export default function UserReport() {
  const { pos, loading: posLoading } = usePOs();
  const { shipments, loading: logLoading } = useLogistics();
  const { users, loading: usersLoading } = useUsers();

  // Filter states
  const [selectedUser, setSelectedUser] = useState("All");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  const wonPos = pos.filter(po => po.status === "Won");
  const loading = posLoading || logLoading || usersLoading;

  // Filter users to Procurement or Manager role
  const eligibleUsers = users
    .filter(u => u.roles && (u.roles.includes("Procurement") || u.roles.includes("Manager")) && !u.deleted && u.status !== "Disabled")
    .map(u => {
      const emailPrefix = u.email ? u.email.split("@")[0].toLowerCase() : "";
      return {
        id: u.id,
        name: u.name,
        email: u.email || "",
        emailPrefix: emailPrefix,
        keys: [emailPrefix, u.email?.toLowerCase(), u.name?.toLowerCase()].filter(Boolean)
      };
    });

  const getPoMarkupRate = (po) => {
    if (po.markupRate !== undefined && po.markupRate !== null) {
      return parseFloat(po.markupRate);
    }
    const savedMarkup = localStorage.getItem("global_markup_rate");
    return savedMarkup !== null ? parseFloat(savedMarkup) : 15;
  };

  const getFilteredData = () => {
    let lineItemsCount = 0;
    let spent = 0;
    let totalClientPrice = 0;
    const itemsList = [];

    const startObj = startDate ? new Date(startDate) : null;
    const endObj = endDate ? new Date(endDate) : null;
    if (endObj) endObj.setHours(23, 59, 59, 999);

    wonPos.forEach((po) => {
      const shipment = shipments.find(s => s.poId === po.id);
      const markup = getPoMarkupRate(po);
      const items = po.items || [];

      items.forEach((item) => {
        const itemStaffRaw = (item.staff || "").trim().toLowerCase();
        if (!itemStaffRaw) return; // Skip if no user is assigned to this line item

        // Find which eligible user owns this item
        const itemUserObj = eligibleUsers.find(u => u.keys.includes(itemStaffRaw));
        if (!itemUserObj) return; // Skip if user is not a Manager or Procurement staff

        // If filter is specific, skip if it doesn't match selected user's name or prefix
        if (selectedUser !== "All" && selectedUser !== itemUserObj.name) {
          return;
        }

        const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
          { id: 'default', vendor: item.vendor || "", quantity: item.quantity || 0, vendorPrice: item.vendorPrice || 0, shippingCost: item.shippingCost || 0, url: item.url || "" }
        ];

        itemVendors.forEach((v) => {
          const key = v.id && v.id !== 'default' ? `${item.id}_${v.id}` : item.id;
          const itemStatusObj = shipment?.itemStatuses?.[key];
          const purchaseDateStr = itemStatusObj?.orderedDate || shipment?.orderedDate || po.awardedDate || po.date;
          if (!purchaseDateStr) return;

          const purchaseDate = new Date(purchaseDateStr);

          // Filter by date range
          if (startObj && purchaseDate < startObj) return;
          if (endObj && purchaseDate > endObj) return;

          const qty = Number(v.quantity || 0);
          const vPrice = Number(v.vendorPrice || 0);
          const sCost = Number(v.shippingCost || 0);

          const lineVendorCost = qty * vPrice;
          const lineTotalCost = lineVendorCost + sCost;
          const lineClientPrice = item.unitPrice 
            ? (Number(item.unitPrice) * (qty / Math.max(1, Number(item.quantity || 1))))
            : (lineTotalCost * (1 + markup / 100) + (Number(po.estShippingCost || 0) > 0 ? (Number(po.estShippingCost) / items.length) : 0) * (qty / Math.max(1, Number(item.quantity || 1))));
          const lineProfit = lineClientPrice - lineTotalCost;
          const lineMargin = lineClientPrice > 0 ? (lineProfit / lineClientPrice) * 100 : 0;

          lineItemsCount++;
          spent += lineTotalCost;
          totalClientPrice += lineClientPrice;

          itemsList.push({
            id: `${po.id}-${key}`,
            poNumber: po.poNumber,
            description: item.description || item.name || "Unnamed Item",
            staffName: itemUserObj.name,
            date: purchaseDateStr,
            quantity: qty,
            unitCost: vPrice,
            totalCost: lineTotalCost,
            clientPrice: lineClientPrice,
            marginPercent: lineMargin,
            marginDollar: lineProfit
          });
        });
      });
    });

    itemsList.sort((a, b) => new Date(b.date) - new Date(a.date));

    const totalProfit = totalClientPrice - spent;
    const marginPercent = totalClientPrice > 0 ? (totalProfit / totalClientPrice) * 100 : 0;

    return {
      lineItemsCount,
      spent,
      marginPercent,
      itemsList
    };
  };

  const { lineItemsCount, spent, marginPercent, itemsList } = getFilteredData();

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "4rem", flexDirection: "column", gap: "1rem" }}>
        <Loader2 className="spinner-icon" size={32} style={{ color: "var(--primary)", animation: "spin 1s linear infinite" }} />
        <p style={{ color: "var(--text-secondary)" }}>Compiling team reports...</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in user-report-wrapper">
      <div className="user-report-controls card">
        <div className="form-group user-filter-dropdown">
          <label className="form-label">Select Staff Member (Procurement/Manager)</label>
          <select 
            className="form-input" 
            value={selectedUser} 
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="All">All Staff Members</option>
            {eligibleUsers.map(u => (
              <option key={u.id} value={u.name}>{u.name} ({u.emailPrefix})</option>
            ))}
          </select>
        </div>
        <div className="form-group user-filter-date">
          <label className="form-label">Start Date</label>
          <input 
            type="date" 
            className="form-input" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="form-group user-filter-date">
          <label className="form-label">End Date</label>
          <input 
            type="date" 
            className="form-input" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button 
          className="btn btn-secondary user-reset-btn" 
          onClick={() => { setStartDate(""); setEndDate(""); setSelectedUser("All"); }}
        >
          Reset Filters
        </button>
      </div>

      <div className="metrics-grid user-metrics-grid">
        {/* Line Items Processed */}
        <div className="metric-card glass-card">
          <div className="metric-header">
            <h3>Line Items Processed</h3>
            <div className="icon-box-muted"><ShoppingBag size={20} /></div>
          </div>
          <div className="metric-value small-val">
            {lineItemsCount} items
          </div>
          <div className="metric-footer user-metric-footer">
            Total PO line items managed
          </div>
        </div>

        {/* Total Cost Spend */}
        <div className="metric-card glass-card">
          <div className="metric-header">
            <h3>Total Procurement Spend</h3>
            <div className="icon-box-muted"><DollarSign size={20} /></div>
          </div>
          <div className="metric-value small-val" style={{ color: "var(--primary)" }}>
            ${spent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="metric-footer user-metric-footer">
            Includes line item cost & shipping totals
          </div>
        </div>

        {/* Average Margin Percentage */}
        <div className="metric-card glass-card">
          <div className="metric-header">
            <h3>Average Est. Margin</h3>
            <div className="icon-box-muted"><TrendingUp size={20} /></div>
          </div>
          <div className="metric-value small-val" style={{ color: marginPercent >= 0 ? "var(--success)" : "var(--danger)" }}>
            {marginPercent.toFixed(1)}%
          </div>
          <div className="metric-footer user-metric-footer">
            Based on Client Price - Total Cost
          </div>
        </div>
      </div>

      <div className="reports-table-container">
        <h3>User Purchase Orders & Line Items Listing ({itemsList.length})</h3>
        <div className="table-responsive">
          <table className="reports-table">
            <thead>
              <tr>
                <th className="user-table-first-header">PO Number</th>
                <th>Purchase Date</th>
                <th>Assigned User</th>
                <th>Item Description</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit Cost</th>
                <th className="text-right">Total Cost</th>
                <th className="text-right">Client Price</th>
                <th className="text-right">Margin ($)</th>
                <th className="text-right">Margin (%)</th>
              </tr>
            </thead>
            <tbody>
              {itemsList.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-state">No matching line items found for selected staff in selected range.</td>
                </tr>
              ) : (
                itemsList.map((item) => (
                  <tr key={item.id}>
                    <td className="user-table-first-col">{item.poNumber || "Draft PO"}</td>
                    <td>{item.date}</td>
                    <td>{item.staffName || "—"}</td>
                    <td>{item.description}</td>
                    <td className="text-right">{item.quantity}</td>
                    <td className="text-right">${item.unitCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-right">${item.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-right">${item.clientPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-right" style={{ color: item.marginDollar >= 0 ? "var(--success)" : "var(--danger)" }}>
                      ${item.marginDollar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-right">
                      <span className={`profit-badge ${item.marginPercent >= 20 ? "high-margin" : item.marginPercent >= 10 ? "med-margin" : "low-margin"}`}>
                        {item.marginPercent.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .user-report-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          align-items: flex-end;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
          background-color: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
        }

        .user-filter-dropdown {
          margin-bottom: 0 !important;
          flex: 1 1 280px;
        }

        .user-filter-date {
          margin-bottom: 0 !important;
          flex: 1 1 180px;
        }

        .user-report-controls select,
        .user-report-controls input {
          margin: 0 !important;
        }

        .user-reset-btn {
          height: 42px;
          white-space: nowrap;
        }
        
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
        }

        .user-metrics-grid {
          margin-bottom: 2rem;
        }

        .metric-card {
          padding: 1.5rem;
          border-radius: var(--radius-md);
          border: 1px solid var(--border-color);
          background-color: var(--bg-card);
          box-shadow: var(--shadow-sm);
        }

        .metric-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .metric-header h3 {
          margin: 0;
          font-size: 0.9rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .metric-value {
          font-family: var(--font-heading);
          font-size: 2.2rem;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .small-val {
          font-size: 1.65rem;
          color: var(--text-primary);
        }

        .metric-footer {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .user-metric-footer {
          color: var(--text-secondary) !important;
          margin-top: 0.5rem;
        }

        .icon-box-muted {
          background: var(--bg-input);
          color: var(--primary);
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
        }

        .reports-table-container {
          background-color: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
        }

        .reports-table-container h3 {
          margin-top: 0;
          margin-bottom: 1.25rem;
          font-size: 1.2rem;
          color: var(--text-primary);
        }

        .table-responsive {
          overflow-x: auto;
        }

        .reports-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .reports-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background-color: var(--bg-input);
          color: var(--text-secondary);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--border-color);
        }

        .user-table-first-header {
          padding-left: 1rem !important;
        }

        .reports-table td {
          padding: 1rem;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-primary);
        }

        .user-table-first-col {
          padding-left: 1rem !important;
          font-weight: 600;
        }

        .reports-table tr:hover td {
          background-color: hsla(var(--hue-primary), 89%, 60%, 0.05);
        }

        .reports-table tr:last-child td {
          border-bottom: none;
        }

        .text-right {
          text-align: right !important;
        }

        .empty-state {
          text-align: center;
          padding: 3rem !important;
          color: var(--text-muted) !important;
        }

        .profit-badge {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 700;
        }

        .high-margin {
          background-color: hsla(var(--hue-success), 70%, 50%, 0.2);
          color: var(--success);
        }

        .med-margin {
          background-color: hsla(var(--hue-primary), 85%, 60%, 0.15);
          color: var(--primary);
        }

        .low-margin {
          background-color: rgba(255, 165, 0, 0.2);
          color: orange;
        }
      `}</style>
    </div>
  );
}
